import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LogOut, Plus, Pencil, Trash2, Mail, FileText, Users, Inbox,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";

const CATEGORIES = ["Thought Leadership", "Safeguarding", "Service Transformation", "Leadership", "Research", "Guidance"];
const emptyPost = { title: "", excerpt: "", content: "", category: "Thought Leadership", cover_image: "", published: true };

const Dashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [enquiries, setEnquiries] = useState([]);
  const [posts, setPosts] = useState([]);
  const [subs, setSubs] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyPost);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [e, p, s] = await Promise.all([
        api.get("/enquiries"),
        api.get("/posts?include_unpublished=true"),
        api.get("/newsletter"),
      ]);
      setEnquiries(e.data);
      setPosts(p.data);
      setSubs(s.data);
    } catch {
      toast.error("Could not load data.");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const doLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  const openNew = () => { setEditing(null); setForm(emptyPost); setDialogOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...p }); setDialogOpen(true); };

  const savePost = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/posts/${editing.id}`, form);
        toast.success("Post updated.");
      } else {
        await api.post("/posts", form);
        toast.success("Post created.");
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const deletePost = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    try {
      await api.delete(`/posts/${id}`);
      toast.success("Post deleted.");
      load();
    } catch {
      toast.error("Delete failed.");
    }
  };

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="min-h-screen bg-cream" data-testid="admin-dashboard">
      {/* Top bar */}
      <header className="sticky top-0 z-30 bg-navy text-cream">
        <div className="mx-auto max-w-7xl px-6 py-4 flex items-center justify-between">
          <div className="flex items-baseline gap-2">
            <span className="font-serif text-xl">Epic</span>
            <span className="text-[0.6rem] tracking-[0.28em] uppercase text-gold font-semibold pb-0.5">Admin</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-cream/70 hidden sm:inline">{user?.email}</span>
            <Button onClick={doLogout} data-testid="logout-btn" variant="outline" className="rounded-full border-cream/30 text-cream hover:bg-cream hover:text-navy h-9">
              <LogOut size={15} className="mr-2" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-6 py-10">
        {/* Stat cards */}
        <div className="grid gap-5 sm:grid-cols-3 mb-10">
          {[
            { icon: Inbox, label: "Enquiries", value: enquiries.length },
            { icon: FileText, label: "Insights", value: posts.length },
            { icon: Users, label: "Subscribers", value: subs.length },
          ].map((s) => (
            <div key={s.label} className="rounded-3xl border border-slate-200 bg-white p-6 flex items-center gap-4">
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-navy text-gold"><s.icon size={22} /></span>
              <div>
                <p className="text-3xl font-serif text-navy">{s.value}</p>
                <p className="text-sm text-slate-500">{s.label}</p>
              </div>
            </div>
          ))}
        </div>

        <Tabs defaultValue="enquiries">
          <TabsList className="mb-6">
            <TabsTrigger value="enquiries" data-testid="tab-enquiries">Enquiries</TabsTrigger>
            <TabsTrigger value="insights" data-testid="tab-insights">Insights</TabsTrigger>
            <TabsTrigger value="newsletter" data-testid="tab-newsletter">Newsletter</TabsTrigger>
          </TabsList>

          {/* Enquiries */}
          <TabsContent value="enquiries">
            <div className="rounded-3xl border border-slate-200 bg-white overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Message</TableHead>
                    <TableHead>Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {enquiries.length === 0 ? (
                    <TableRow><TableCell colSpan={4} className="text-center text-slate-400 py-10">No enquiries yet.</TableCell></TableRow>
                  ) : enquiries.map((e) => (
                    <TableRow key={e.id} data-testid={`enquiry-row-${e.id}`}>
                      <TableCell>
                        <p className="font-medium text-navy">{e.name}</p>
                        <a href={`mailto:${e.email}`} className="text-xs text-slate-500 flex items-center gap-1"><Mail size={11} />{e.email}</a>
                        {e.organisation && <p className="text-xs text-slate-400">{e.organisation}</p>}
                      </TableCell>
                      <TableCell><Badge variant="secondary" className="font-normal">{e.enquiry_type}</Badge></TableCell>
                      <TableCell className="max-w-xs"><p className="text-sm text-slate-600 truncate">{e.message}</p>{e.preferred_date && <p className="text-xs text-gold mt-1">Preferred: {e.preferred_date}</p>}</TableCell>
                      <TableCell className="text-xs text-slate-400 whitespace-nowrap">{new Date(e.created_at).toLocaleDateString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* Insights */}
          <TabsContent value="insights">
            <div className="flex justify-end mb-4">
              <Button onClick={openNew} data-testid="new-post-btn" className="rounded-full bg-navy text-cream">
                <Plus size={16} className="mr-2" /> New Insight
              </Button>
            </div>
            <div className="rounded-3xl border border-slate-200 bg-white overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {posts.map((p) => (
                    <TableRow key={p.id} data-testid={`post-row-${p.id}`}>
                      <TableCell className="font-medium text-navy max-w-sm truncate">{p.title}</TableCell>
                      <TableCell><Badge variant="secondary" className="font-normal">{p.category}</Badge></TableCell>
                      <TableCell>
                        {p.published ? <Badge className="bg-teal text-cream">Published</Badge> : <Badge variant="outline">Draft</Badge>}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="ghost" onClick={() => openEdit(p)} data-testid={`edit-post-${p.id}`}><Pencil size={15} /></Button>
                        <Button size="sm" variant="ghost" onClick={() => deletePost(p.id)} data-testid={`delete-post-${p.id}`} className="text-destructive"><Trash2 size={15} /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* Newsletter */}
          <TabsContent value="newsletter">
            <div className="rounded-3xl border border-slate-200 bg-white overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow><TableHead>Email</TableHead><TableHead>Subscribed</TableHead></TableRow>
                </TableHeader>
                <TableBody>
                  {subs.length === 0 ? (
                    <TableRow><TableCell colSpan={2} className="text-center text-slate-400 py-10">No subscribers yet.</TableCell></TableRow>
                  ) : subs.map((s) => (
                    <TableRow key={s.id}><TableCell className="text-navy">{s.email}</TableCell><TableCell className="text-xs text-slate-400">{new Date(s.created_at).toLocaleDateString()}</TableCell></TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Post editor dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="post-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-navy">{editing ? "Edit Insight" : "New Insight"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={savePost} className="space-y-4">
            <div className="space-y-2">
              <Label>Title *</Label>
              <Input required data-testid="post-title" value={form.title} onChange={(e) => setField("title", e.target.value)} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setField("category", v)}>
                  <SelectTrigger data-testid="post-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-3 pt-7">
                <Switch checked={form.published} onCheckedChange={(v) => setField("published", v)} data-testid="post-published" />
                <Label className="cursor-pointer">Published</Label>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Cover image URL</Label>
              <Input data-testid="post-cover" value={form.cover_image} onChange={(e) => setField("cover_image", e.target.value)} placeholder="https://..." />
            </div>
            <div className="space-y-2">
              <Label>Excerpt</Label>
              <Textarea rows={2} data-testid="post-excerpt" value={form.excerpt} onChange={(e) => setField("excerpt", e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Content</Label>
              <Textarea rows={7} data-testid="post-content" value={form.content} onChange={(e) => setField("content", e.target.value)} placeholder="Use blank lines to separate paragraphs." />
            </div>
            <DialogFooter>
              <Button type="submit" disabled={saving} data-testid="save-post" className="rounded-full bg-navy text-cream">
                {saving ? "Saving..." : "Save Insight"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;
